import type { QuyenChungTu, QuyenChungTuNgay } from '@/types/schemas';
import { generateSoChungTuHienTai } from '@/lib/stringUtil';

export const getDocumentBookKey = (quyenChungTu?: QuyenChungTu | null) =>
  quyenChungTu?.uuid || quyenChungTu?.ma_nk || '';

export const normalizeDocumentDate = (value: unknown): string => {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toISOString().split('T')[0];
  }

  if (typeof value === 'string' && value.trim()) {
    const rawValue = value.trim();
    const dateValue = new Date(rawValue);

    if (!Number.isNaN(dateValue.getTime())) {
      return dateValue.toISOString().split('T')[0];
    }

    return rawValue.split('T')[0];
  }

  return new Date().toISOString().split('T')[0];
};

const getMaxNumber = (records: QuyenChungTuNgay[]) =>
  records.reduce((maxNumber, record) => Math.max(maxNumber, Number(record.i_so_ct_ht) || 0), 0);

const isSameDay = (date: Date, target: Date) =>
  date.getFullYear() === target.getFullYear() &&
  date.getMonth() === target.getMonth() &&
  date.getDate() === target.getDate();

const isSameMonth = (date: Date, target: Date) =>
  date.getFullYear() === target.getFullYear() && date.getMonth() === target.getMonth();

const isSameQuarter = (date: Date, target: Date) => {
  const quarter = Math.floor(date.getMonth() / 3);
  const targetQuarter = Math.floor(target.getMonth() / 3);
  return date.getFullYear() === target.getFullYear() && quarter === targetQuarter;
};

const isSameYear = (date: Date, target: Date) => date.getFullYear() === target.getFullYear();

export const getCurrentDocumentNumber = (quyenChungTu: QuyenChungTu, documentDate: string): number => {
  const records = quyenChungTu.ngay || [];

  if (!records.length) {
    return (Number(quyenChungTu.i_so_ct_ht) || 0) + 1;
  }

  const targetDate = new Date(documentDate);
  if (Number.isNaN(targetDate.getTime())) {
    return getMaxNumber(records) + 1;
  }

  const duplicateMode = Number(quyenChungTu.kieu_trung_so);
  const findRecordNumber = (predicate: (date: Date) => boolean) => {
    const matchedRecords = records.filter(record => {
      const recordDate = new Date(record.ngay_nk);
      return !Number.isNaN(recordDate.getTime()) && predicate(recordDate);
    });

    return getMaxNumber(matchedRecords);
  };

  switch (duplicateMode) {
    case 1:
      return findRecordNumber(date => isSameDay(date, targetDate)) + 1;
    case 2:
      return findRecordNumber(date => isSameMonth(date, targetDate)) + 1;
    case 3:
      return findRecordNumber(date => isSameQuarter(date, targetDate)) + 1;
    case 4:
      return findRecordNumber(date => isSameYear(date, targetDate)) + 1;
    case 0:
    default:
      return getMaxNumber(records) + 1;
  }
};

export const generateDocumentNumber = (quyenChungTu: QuyenChungTu, documentDate: string): string => {
  const nextNumber = getCurrentDocumentNumber(quyenChungTu, documentDate);
  return generateSoChungTuHienTai(nextNumber, quyenChungTu.so_ct_mau || '[######]', documentDate);
};
